import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '@/services/api';
import { useTerritoryStore } from '@/stores/territoryStore';

/**
 * RenovationSimulator — simulateur de campagne de renovation (panneau droit).
 * L'utilisateur coche des leviers d'aides ; POST /simulations recalcule les logements sortis
 * du statut passoire, l'energie & le CO2 economises, les menages sortis de precarite, le cout public.
 *
 * Valeurs de demonstration dans la maquette.
 */

export interface RenoLever {
  id: string;
  label: string;
  desc: string;
  units: number;
  gwh: number;
  prec: number;
}

export const RENO_LEVERS: RenoLever[] = [
  { id: 'opah',    label: "OPAH centre ancien",          desc: "Operation programmee d'amelioration",      units: 320, gwh: 4.2, prec: 140 },
  { id: 'ite',     label: 'ITE parc social',             desc: "Isolation par l'exterieur · bailleurs",    units: 210, gwh: 3.1, prec: 95  },
  { id: 'guichet', label: "Guichet France Renov'",       desc: 'Accompagnement + ingenierie',              units: 180, gwh: 1.8, prec: 70  },
  { id: 'avance',  label: "Avance MaPrimeRenov'",        desc: 'Menages modestes · reste a charge',        units: 240, gwh: 2.4, prec: 160 },
  { id: 'fioul',   label: 'Sortie du chauffage fioul',   desc: 'Pompe a chaleur · reseau de chaleur',      units: 150, gwh: 1.6, prec: 40  },
];

export interface SimResult {
  renovated_units: number;
  gwh_saved: number;
  co2_avoided: number;
  households_out_of_precarity: number;
  public_cost_eur: number;
  by_lever: { id: string; label: string; units: number }[];
}

interface RenovationSimulatorProps {
  onResult?: (r: SimResult | null) => void;
}

export function RenovationSimulator({ onResult }: RenovationSimulatorProps) {
  const { activeTerritory } = useTerritoryStore();
  const [selected, setSelected] = useState<string[]>([]);

  const sim = useMutation<SimResult, Error, string[]>({
    mutationFn: async (levers: string[]) => {
      const { data } = await api.post<SimResult>('/simulations', {
        territory_code: activeTerritory?.code_insee,
        name: `sim-renovation_campaign-${Date.now()}`,
        type: 'renovation_campaign',
        parameters: { levers },
      });
      return data;
    },
    onSuccess: (r) => onResult?.(r),
  });

  const toggle = (id: string) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    sim.reset();
    onResult?.(null);
  };

  const r = sim.data;
  const canSimulate = selected.length > 0 && !sim.isPending;
  const eur = (n: number) => (n / 1_000_000).toFixed(1).replace('.', ',') + ' M€';

  return (
    <div className="flex flex-col gap-3 rounded-panel border border-line bg-white p-[18px] shadow-card h-full overflow-y-auto">
      <div>
        <h3 className="font-display text-[15px] font-bold text-ink">Simulateur de campagne</h3>
        <p className="mt-0.5 text-xs text-faint">Leviers d'aides mobilisables</p>
      </div>

      {/* Liste des leviers */}
      <div className="flex flex-col gap-2">
        {RENO_LEVERS.map((a) => {
          const on = selected.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              className={`flex items-center gap-3 rounded-[11px] border px-3 py-2.5 text-left transition ${
                on ? 'border-[#9bb8ee] bg-[#eef4ff]' : 'border-line bg-white hover:border-muted'
              }`}
            >
              {/* Checkbox accessible */}
              <span
                aria-hidden="true"
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
                  on ? 'border-navy bg-navy' : 'border-[#cbd3e2] bg-white'
                }`}
              >
                {on && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </span>
              <span className="flex-1">
                <span className="block text-[13.5px] font-semibold text-ink">{a.label}</span>
                <span className="block text-[11.5px] text-faint">{a.desc}</span>
              </span>
              <span className="flex-shrink-0 font-mono text-xs font-semibold text-[#1f8a5b]">
                +{a.units} logts
              </span>
            </button>
          );
        })}
      </div>

      {/* Bouton simuler */}
      <button
        type="button"
        disabled={!canSimulate}
        onClick={() => sim.mutate(selected)}
        className="mt-1 flex h-[46px] w-full items-center justify-center gap-2 rounded-[11px] text-sm font-bold text-white transition disabled:cursor-not-allowed"
        style={{ background: canSimulate ? '#1b2559' : '#cbd3e2' }}
      >
        {sim.isPending ? 'Calcul…' : 'Simuler la campagne'}
      </button>

      {sim.isError && (
        <p className="text-xs text-[#e1000f]" role="alert">
          Erreur de simulation. Veuillez reessayer.
        </p>
      )}

      {/* Resultats de simulation */}
      {r && (
        <div className="rounded-[13px] border border-[#cfe7d8] bg-gradient-to-b from-[#eef8f1] to-[#e3f3ea] p-[15px]">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1f8a5b]">
                Logements sortis de passoire
              </div>
              <div className="font-display text-[30px] font-extrabold leading-none text-[#1f8a5b]">
                {r.renovated_units.toLocaleString('fr-FR')}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-[#5b6480]">Cout public indicatif</div>
              <div className="font-display text-lg font-bold text-ink">{eur(r.public_cost_eur)}</div>
              <div className="text-[9px] text-[#5b6480]">Cout indicatif — France Renov'</div>
            </div>
          </div>

          {/* Stats secondaires */}
          <div className="mt-3 flex gap-2.5">
            <StatBox label="Energie" value={`${String(r.gwh_saved).replace('.', ',')} GWh`} />
            <StatBox label="Menages" value={`−${r.households_out_of_precarity}`} />
            <StatBox label="CO₂" value={`${r.co2_avoided.toLocaleString('fr-FR')} t`} />
          </div>

          {/* Detail par levier (recharts) */}
          {r.by_lever.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-[11px] font-semibold text-[#5b6480]">Detail par levier</p>
              <ResponsiveContainer width="100%" height={r.by_lever.length * 28 + 16}>
                <BarChart
                  data={r.by_lever}
                  layout="vertical"
                  margin={{ left: 8, right: 24, top: 0, bottom: 0 }}
                >
                  <XAxis type="number" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fontSize: 9, fill: '#5b6480' }}
                    width={120}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    itemStyle={{ fontSize: 11 }}
                    labelStyle={{ fontSize: 11 }}
                    wrapperStyle={{ zIndex: 10 }}
                  />
                  <Bar dataKey="units" fill="#1b2559" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 rounded-[9px] bg-white/60 px-2.5 py-2">
      <div className="text-[10px] text-[#5b6480]">{label}</div>
      <div className="font-display text-sm font-bold text-[#1f8a5b]">{value}</div>
    </div>
  );
}
